import { useState } from "react";
import { Loader2, Check, Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Container, PageHero } from "./_ui";
import { Seo } from "@/components/marketing/Seo";

const inputCls =
  "mt-1.5 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition-colors placeholder:text-neutral-400 focus:border-neutral-900";

export default function Kontakt() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "", hp: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Fyll inn navn, e-post og melding.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Noe gikk galt. Prøv igjen eller send e-post til post@murphy.no.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo title="Kontakt oss" description="Ta kontakt med Murphy Styrerom for demo eller spørsmål. Vi viser deg styrerommet med dine egne selskaper på 20 minutter." path="/kontakt" />
      <PageHero label="Kontakt" title="Snakk med oss.">
        Book en demo eller still et spørsmål. Vi svarer så fort vi kan — som regel samme dag.
      </PageHero>

      <Container className="grid gap-12 py-10 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-2xl border border-neutral-200 p-7 sm:p-9">
          {sent ? (
            <div className="flex flex-col items-start gap-4" data-testid="contact-success">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-[#CDF546]"><Check className="h-6 w-6 text-neutral-900" /></div>
              <h2 className="font-headings text-2xl font-bold text-neutral-900">Takk for henvendelsen!</h2>
              <p className="text-sm text-neutral-600">Vi har mottatt meldingen din og tar kontakt på {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input type="text" value={form.hp} onChange={upd("hp")} name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Navn *</label>
                  <input data-testid="contact-name" className={inputCls} value={form.name} onChange={upd("name")} placeholder="Ola Nordmann" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700">E-post *</label>
                  <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={upd("email")} placeholder="ola@selskap.no" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Selskap</label>
                  <input data-testid="contact-company" className={inputCls} value={form.company} onChange={upd("company")} placeholder="Selskap AS" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Telefon</label>
                  <input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={upd("phone")} placeholder="+47 400 00 000" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Melding *</label>
                <textarea data-testid="contact-message" rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={upd("message")} placeholder="Fortell kort hva du lurer på, eller be om en demo." />
              </div>
              {error ? <p className="text-sm text-red-600" data-testid="contact-error">{error}</p> : null}
              <button type="submit" disabled={sending} data-testid="contact-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-700 disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {sending ? "Sender …" : "Send henvendelse"}
              </button>
            </form>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="font-headings text-lg font-bold text-neutral-900">Murphy AS</h3>
            <ul className="mt-4 space-y-3 text-sm text-neutral-600">
              <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-neutral-900" /> Torvgata 25, 2000 Lillestrøm</li>
              <li className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-neutral-900" /> <a href="mailto:post@murphy.no" className="hover:text-neutral-900">post@murphy.no</a></li>
              <li className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-neutral-900" /> <a href="tel:+4740031163" className="hover:text-neutral-900">+47 400 31 163</a></li>
            </ul>
          </div>
          <div className="rounded-2xl bg-[#CDF546] p-6">
            <p className="font-headings text-lg font-bold text-neutral-900">20 minutter er nok.</p>
            <p className="mt-2 text-sm text-neutral-800">Vi setter opp styrerommet med dine egne selskaper, så ser du verdien med en gang.</p>
          </div>
        </div>
      </Container>
    </>
  );
}
